import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Course } from "./interfaces";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { trackEventGA, trackEventGTM } from "lib/amplitude";
import { useIsSubdomain, useIsTenant } from "lib/host";
import { useAuthStore } from "modules/Auth/authStore";
import { useCartStore } from "modules/Business/Selection/cartStore";
import { IconPlusCircle } from "components/Icon/IconPlusCircle";
import dynamic from "next/dynamic";
import { useAvatarStore } from "components/InteractiveAvatar/avatarStore";
import Image from "next/image";

const VideoJS = dynamic(
  () => import("modules/Course/CourseVideoJs").then((mod) => mod.VideoJS),
  {
    ssr: false,
  }
);

type PropsCard = Course & {
  isUncheck?: boolean;
  isHide?: boolean;
  isRow?: boolean;
  isMyClass?: boolean;
  isSquare?: boolean;
  tracker?: any;
  wrapClass?: string;
  isRecommendPage?: boolean;
  onClickFunction?: () => void;
  isModal?: boolean;
};

const CardCourseSectionMobile: React.FC<PropsCard> = ({
  isUncheck = false,
  isHide = false,
  className = "",
  isRow = false,
  isMyClass = false,
  isSquare = false,
  tracker,
  wrapClass = "",
  isRecommendPage = false,
  onClickFunction,
  isModal = false,
  ...props
}) => {
  const [chapters, setChapters] = useState([]);
  const [countLesson, setCountLesson] = useState(0);
  const [isLoading, setLoading] = useState(false);

  const { t } = useTranslation("common");
  const isB2B = useIsTenant() || useIsSubdomain();
  const isBusiness = !isB2B;

  useEffect(() => {
    if (isRow && props?.chapters && props?.chapters.length) {
      setLoading(true);

      const newChapters = props?.chapters.map((el: any) => {
        const allLesson =
          el?.lessons && el?.lessons.length
            ? el?.lessons.map((items: any) => ({
                image: props?.image_cover_url,
                course_slug: props.slug,
                ...items,
              }))
            : [];

        return {
          name: el.name,
          image: props?.image_cover_url,
          lessons: allLesson,
        };
      });

      let counting: any = props?.chapters.flatMap(
        (el: any) => el?.lessons && el?.lessons.length
      );

      setCountLesson(counting.length);
      setChapters(newChapters);
      setLoading(false);
    }
  }, [props.chapters, isRow]);

  const events = useMemo(
    () => ({
      event: "course_hover_a",
      property: {
        course_name: props?.name ?? "",
        course_slug: props?.slug ?? "",
        section_name: props?.sectionName ?? "",
        institution_name:
          (props as any)?.institution_name ?? props.institution?.name ?? "",
        index: props?.index ?? 0,
        page: `B2B Homepage`,
      },
    }),
    [props]
  );

  const [isHovering, setIsHovered] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [timeout, setModalTimeout] = useState(null);
  const [videoTimeout, setVideoTimeout] = useState(null);

  useEffect(() => {
    setVideoURL((props as any)?.hover_content_url);
  }, [isHovering]);

  const mouseEnterHandler = useCallback(() => {
    timeout && clearTimeout(timeout);
    videoTimeout && clearTimeout(timeout);
    setModalTimeout(
      setTimeout(() => {
        trackEventGTM({
          ...(events as any),
        });
        trackEventGA({
          ...(events as any),
        });

        trackEventGTM({
          ...(events as any),
        });

        trackEventGA({
          ...(events as any),
        });
      }, 3000)
    );

    setVideoTimeout(
      setTimeout(() => {
        setIsHovered(isHide ? false : true);
      }, 500)
    );
  }, [events, isHide, timeout, videoTimeout]);

  const mouseLeaveHandler = useCallback(() => {
    timeout && clearTimeout(timeout);
    videoTimeout && clearTimeout(videoTimeout);
    setIsHovered(false);
  }, [timeout, videoTimeout]);

  const router = useRouter();
  const hasBusinessCourse =
    typeof router.query.hasBusinessCourse == "string"
      ? router.query.hasBusinessCourse
      : "false";

  const { loginState, currentUser } = useAuthStore((state) => ({
    loginState: state.loginState,
    currentUser: state.currentUser,
  }));

  const tenantJpTracker =
    (currentUser?.tenant !== undefined &&
      [
        "ppsdmaparatur",
        "menpan",
        "wonderfullearning",
        "kemkes",
        "tapinkab",
        "bssn",
        "bkpsdm-bekasikota",
        // "maluku-gate",
        // "trenggalekkab",
        "bpsdm-sumbar",
        "bpsdm-sulsel",
        "bpsdm-jambi",
        "bpsdmdki",
        // "jambikota",
        // "muarojambikab",
        // "pesawarankab",
        "batam",
        "pusdiklat-komdigi",
        "pusdiklat-kemenkeu",
        "kemenperin",
      ].includes((currentUser?.tenant as any)?.slug)) ||
    [
      "ppsdmaparatur",
      "menpan",
      "wonderfullearning",
      "kemkes",
      "tapinkab",
      "bssn",
      "bkpsdm-bekasikota",
      // "maluku-gate",
      // "trenggalekkab",
      "bpsdm-sumbar",
      "bpsdm-sulsel",
      "bpsdm-jambi",
      "bpsdmdki",
      // "jambikota",
      // "muarojambikab",
      // "pesawarankab",
      "batam",
      "pusdiklat-komdigi",
      "pusdiklat-kemenkeu",
      "kemenperin",
    ].includes(currentUser?.referral);

  const gaiaMultipleAccess =
    (currentUser?.tenant !== undefined &&
      ["gaia", "mega-insurance-moodle", "pupuk-indonesia"].includes(
        (currentUser?.tenant as any)?.slug
      )) ||
    ["gaia", "mega-insurance-moodle", "pupuk-indonesia"].includes(
      currentUser?.referral
    );

  const updateCart = useCartStore((state) => state.updateCart);

  let content = useCallback(
    () =>
      videoURL ? (
        <VideoJS
          isHovering={isHovering}
          url={videoURL}
          handleInterrupt={() => {}}
        />
      ) : null,
    [videoURL, isHovering]
  );

  const [isHoveringGaia, setIsHoveringGaia] = useState(false);
  const { setIsCourseTranscript, setCourseTranscript } = useAvatarStore();

  return (
    <div
      className={(!isRow ? "cursor-pointer" : "") + " relative h-full w-full"}
      onClick={
        onClickFunction
          ? onClickFunction
          : () => {
              if (!isRow) {
                if ((props as any).onSelect) {
                  updateCart(props);
                } else {
                  router.push(
                    loginState == "LOGIN"
                      ? isRecommendPage
                        ? {
                            pathname: "/r/[page]",
                            query: {
                              page: `/course/${props.slug}`,
                            },
                          }
                        : {
                            pathname: "/course/[courseSlug]",
                            query: { courseSlug: props?.slug },
                          }
                      : {
                          pathname: "/login",
                          query: {
                            url: encodeURIComponent(`/course/${props?.slug}`),
                          },
                        }
                  );
                }
              }

              if (tracker) {
                trackEventGTM(tracker);
                trackEventGA(tracker);
              }
            }
      }
    >
      {/* {(props.slug ===
        "gak-butuh-peluang-besar-ini-alasan-kenapa-anda-belum-sukses" ||
        props.slug === "9-menit-yg-akan-mengubah-hidupmu" ||
        props.slug ===
          "kenapa-cari-duit-susah-part-3-surface-vs-deeper-level" ||
        props.slug === "kenapa-jaman-sekarang-susah-cari-duit" ||
        props.slug === "ujian-hidup-yang-akan-membuatmu-naik-level") &&
        (gaiaMultipleAccess || CONST.IS_DEV) && (
          <div className="absolute z-[2] top-8 right-8 w-full flex justify-end items-end">
            <div
              onClick={(e) => {
                e.stopPropagation();

                trackEventGTM({
                  event: "gaia_course_chip_a",
                  property: {
                    user_id: currentUser?.id ?? "",
                    is_logged_in: currentUser ? true : false,
                    course_slug: props.slug,
                    lesson_slug: (props?.chapters?.[0] as any)?.lessons[0]
                      ?.slug,
                    position: "card",
                  },
                });

                trackEventGA({
                  event: "gaia_course_chip_a",
                  property: {
                    user_id: currentUser?.id ?? "",
                    is_logged_in: currentUser ? true : false,
                    course_slug: props.slug,
                    lesson_slug: (props?.chapters?.[0] as any)?.lessons[0]
                      ?.slug,
                    position: "card",
                  },
                });

                setIsCourseTranscript(true);

                if (
                  props.slug ===
                    "gak-butuh-peluang-besar-ini-alasan-kenapa-anda-belum-sukses" ||
                  props.slug === "9-menit-yg-akan-mengubah-hidupmu"
                ) {
                  setCourseTranscript(`
                        Jelaskan secara singkat mengenai :

                        "Bayangkan gaptek itu bisa dipelajari. Anak kecil yang baru lahir pun awalnya gaptek, tapi lama-kelamaan bisa belajar sendiri. Masa kita yang sudah dewasa, dengan otak yang lebih matang, kalah sama anak kecil? Saya baru saja menyelesaikan batch ketiga dari program *Thirty Days Challenge* bersama Kang Afif, di mana kita membahas cara membuat kursus online. Ada hal-hal menarik yang saya pelajari. Misalnya, di batch pertama, seribu orang ikut, batch kedua juga seribu, dan batch ketiga sekitar lima ratusan. Banyak orang ingin naik level secara finansial, punya produk sendiri, brand sendiri, margin besar, dan kebebasan waktu. Tapi, masalahnya bukan pada peluang atau nasib, melainkan pada diri sendiri. Banyak orang gagal karena mereka menghalangi diri sendiri. Saya ingin menyampaikan bahwa peluang di dunia digital, seperti affiliate marketing dan kursus online, sedang naik daun. Statistik global menunjukkan pertumbuhan yang signifikan, bahkan mencapai miliaran dolar. Jadi, kalau ada yang bertanya apakah ini masih prospek 10 tahun ke depan, jawabannya adalah ya. Peluangnya besar, mentornya ada, dan modalnya kecil. Bahkan, Anda bisa memulai hanya dengan handphone. Tapi, banyak orang masih punya alasan seperti, “Saya gaptek,” “Saya tidak punya keahlian,” atau “Saya tidak punya followers.” Padahal, semua itu bisa dipelajari. Anak kecil saja bisa belajar, masa kita tidak? Masalah terbesar sebenarnya adalah mental block. Banyak orang tidak sadar bahwa mereka punya trauma atau pengalaman masa lalu yang menghambat kemajuan mereka. Misalnya, ada peserta di kelas saya yang takut public speaking karena pernah dipermalukan di sekolah saat kecil. Pengalaman itu membekas dan membuatnya takut salah lagi. Padahal, sebagai sales, public speaking adalah hal yang penting. Mental block seperti ini seringkali muncul dari kejadian masa kecil yang kita lupa, tapi dampaknya masih terasa sampai sekarang. Jadi, intinya, kesuksesan bukan hanya tentang peluang di luar, tapi juga tentang kesiapan diri. Jika ada peluang di depan mata, tapi Anda masih merasa takut, ragu, atau tidak percaya diri, coba tanyakan pada diri sendiri: apa yang menghambat saya? Apakah ada cerita masa lalu yang masih membekas? Dengan menyadari dan berdamai dengan pengalaman itu, Anda bisa membuka jalan untuk maju. Saya mengajak Anda untuk mulai introspeksi dan mengambil langkah kecil. Ikuti program pelatihan, belajar hal baru, dan jangan biarkan mental block menghalangi Anda. Peluang sudah ada, tinggal bagaimana Anda menyikapinya. Jika Anda tertarik, jangan ragu untuk menghubungi saya. Terima kasih sudah menonton, dan sampai jumpa di video berikutnya!"
                        
                        Jangan gunakan kata ganti orang ketiga seperti "penulis", tapi buat seolah-olah Anda yang menyampaikan pesan tersebut secara langsung dan berikan pertanyaan sesuai konten itu untuk memulai diskusi.
                        `);
                } else if (
                  props.slug ===
                  "kenapa-cari-duit-susah-part-3-surface-vs-deeper-level"
                ) {
                  setCourseTranscript(`
                        Jelaskan secara singkat mengenai :

                        "Saya ingin membahas tentang alasan-alasan yang sering kita dengar ketika seseorang tidak mengambil peluang. Misalnya, tadi malam saya dan tim di Travel U meluncurkan mini course Rich Affiliate, yang mengajarkan cara menjadi affiliate dan menjual produk digital. Lebih dari dua ribu orang bergabung via Zoom dan YouTube. Namun, ada beberapa orang yang tidak ikut karena alasan seperti tidak punya laptop. Ini menarik karena saya ingin membahas lebih dalam tentang alasan-alasan seperti ini. Ketika seseorang bilang, "Saya tidak punya laptop," atau "Saya tidak punya iPad," itu sebenarnya hanya alasan permukaan (surface level). Di balik itu, ada alasan yang lebih dalam. Misalnya, ketika seseorang bilang tidak punya iPad, sebenarnya masalahnya bukan iPad, tapi uang. Mereka tidak punya uang untuk membeli iPad. Ini adalah deeper level. Tapi, alasan seperti ini sering disembunyikan karena tidak nyaman untuk diungkapkan. Saya sudah lebih dari 30 tahun menjadi sales, dan saya sering menemui orang-orang dengan alasan seperti ini. Ketika seseorang bilang mereka tidak punya skill atau uang, sebenarnya itu hanya permukaan. Jika kita gali lebih dalam, biasanya ada masalah internal seperti rasa malas, trauma, atau mental block. Misalnya, ketika seseorang bilang tidak punya skill, kita bisa tanya, "Skill apa yang kamu butuhkan?" Jawabannya bisa lebih spesifik, seperti "Saya tidak bisa bikin landing page." Nah, dari situ, kita bisa cari solusi. Saya selalu bilang, kita hidup di era abundance, di mana informasi sangat mudah didapat. Jika kamu tidak punya skill, kamu bisa belajar di YouTube. Jika tidak punya uang, kamu bisa cari wifi gratis di stasiun atau tempat umum. Intinya, tidak ada alasan untuk tidak belajar atau mencoba. Jika kamu bilang tidak punya waktu, padahal kamu punya banyak waktu luang, itu artinya kamu malas. Malas adalah deeper level yang sering disembunyikan karena memalukan. Ketika kita menghadapi alasan-alasan seperti ini, kita harus paham bahwa itu hanya permukaan. Masalah sebenarnya ada di internal. Jika kita bisa mengubah mindset internal, maka hidup kita akan berubah. Jadi, jika kamu merasa hidupmu susah, coba lihat kembali mindsetmu. Mungkin ada yang perlu diubah. Semoga penjelasan ini bermanfaat. Terima kasih sudah menonton, dan sampai jumpa di video berikutnya! Bye-bye."

                        Jangan gunakan kata ganti orang ketiga seperti "penulis", tapi buat seolah-olah Anda yang menyampaikan pesan tersebut secara langsung dan berikan pertanyaan sesuai konten itu untuk memulai diskusi.
                      `);
                } else if (
                  props.slug === "kenapa-jaman-sekarang-susah-cari-duit"
                ) {
                  setCourseTranscript(`
                        Jelaskan secara singkat mengenai :

                        Zaman sekarang, banyak orang mengeluh susah cari uang. Bisnis konvensional sepi, persaingan tinggi, dan banyak yang merasa terjebak dalam pola pikir lama. Tapi, sebenarnya masalahnya bukan pada uangnya, melainkan pada cara kita memandang dan memanfaatkan era yang sedang berjalan. Saya sering dengar keluhan seperti, "Dulu bisnis mudah, sekarang susah," atau "Harus punya followers ratusan ribu baru bisa dapat uang." Padahal, enggak harus seperti itu. Saya sudah menulis buku berjudul Rich Creator Blackbook yang membahas mengapa banyak orang kesulitan financially dan bagaimana cara mengatasinya. Intinya, masalah utama adalah edukasi kita yang masih terjebak di era revolusi industri. Sistem pendidikan kita dirancang untuk menghasilkan pekerja, bukan pencipta. Dari kecil, kita diajari untuk sekolah, kuliah, cari kerja, dan dapat gaji. Pola pikir ini sudah ketinggalan zaman. Sekarang kita hidup di era digital dan informasi, di mana uang tidak lagi hanya tentang produk fisik, tapi juga tentang attention dan informasi. Contohnya, Taylor Swift bisa dapat ratusan miliar hanya karena dia bisa menarik perhatian orang. Uang mengikuti attention, bukan sebaliknya. Nah, kalau kita masih berkutat di bisnis konvensional, ya wajar susah. Persaingannya terlalu tinggi. Dulu, mungkin hanya ada sepuluh toko baju di satu kota, sekarang ada ribuan toko online. Supply melimpah, demand tersebar, dan akhirnya potensi keuntungan per individu menurun. Tapi, di dunia digital dan informasi, persaingannya masih relatif sedikit. Masih banyak peluang untuk menghasilkan uang secara pasif, bahkan tanpa harus punya followers ratusan ribu. Saya sendiri membuktikannya. Pagi ini, saya bangun tidur, sarapan, dan cek email. Tanpa harus bikin konten atau live jualan, transaksi terus masuk. Ini karena saya sudah menyiapkan sistem dan produk digital yang bekerja secara otomatis. Saya tidak perlu aktif jualan setiap saat. Ini yang disebut pasif income. Era sekarang memungkinkan kita menghasilkan uang dengan cara yang lebih efisien, asalkan kita mau belajar dan beradaptasi. Elon Musk pernah bilang, di masa depan, kerjaan seperti yang kita kenal sekarang mungkin tidak akan ada lagi. Semua akan digantikan oleh AI dan teknologi. Jadi, tugas kita sekarang adalah belajar dan beradaptasi. Jangan bilang "saya gaptek" atau "saya enggak bisa." Kalau kita tetap bertahan di pola pikir lama, ya kita akan ketinggalan. Era digital menuntut kita untuk memahami informasi, teknologi, dan cara menarik perhatian. Jadi, kalau Anda masih kesulitan cari uang, mungkin itu karena Anda masih terjebak di era lama. Mulai pelajari cara kerja dunia digital. Buku Rich Creator Blackbook bisa jadi panduan Anda. Di dalamnya, saya jelaskan step-by-step bagaimana memanfaatkan era ini untuk menghasilkan uang secara pasif. Enggak perlu takut atau malu belajar hal baru. Yang penting, kita mau maju dan beradaptasi. Jadi, stop mengeluh dan mulai action. Era digital menawarkan banyak peluang, asalkan kita mau mengambil langkah pertama. Semoga penjelasan ini membantu. Jika ingin tahu lebih detail, langsung cek bukunya. Sampai jumpa di video berikutnya!

                        Jangan gunakan kata ganti orang ketiga seperti "penulis", tapi buat seolah-olah Anda yang menyampaikan pesan tersebut secara langsung dan berikan pertanyaan sesuai konten itu untuk memulai diskusi.
                      `);
                } else if (
                  props.slug === "ujian-hidup-yang-akan-membuatmu-naik-level"
                ) {
                  setCourseTranscript(`
                        Jelaskan secara singkat mengenai :

                        Sebagai seorang entrepreneur, ada saat-saat di mana saya harus membuat keputusan penting dan mengambil risiko besar. Ketika saya berusia 20 tahun, memutuskan menjadi entrepreneur terasa mudah karena tidak ada yang bisa hilang. Namun, ketika sudah memiliki banyak hal baik, melepaskannya demi tujuan yang lebih besar tidaklah mudah. Saya pernah menjual bisnis di Singapura dan memutuskan untuk kembali ke Indonesia, memulai semuanya dari nol. Saya memulai dari hal sederhana, bahkan dari garasi rumah yang diubah menjadi kantor. Saat itu, kondisi tempat kerja tidak ideal—sering bocor, listrik mati, dan banjir melanda. Tapi, saya percaya bahwa setiap keputusan baik di awal akan diuji. Ujian itu menguji komitmen dan ketulusan tujuan kita. Saya memulai bisnis di bidang edukasi, dimulai dari event kecil yang mengajarkan leadership dan character development untuk anak-anak dan remaja. Passion saya di pendidikan, terutama untuk generasi muda, menjadi dasar bisnis ini. Dari satu cabang di Puri, sekarang kami memiliki 13 cabang di Surabaya dan Semarang, serta platform digital yang terus berkembang. Semua ini adalah hasil dari komitmen tinggi dan pantang menyerah. Saya percaya, mengambil risiko tidak menjamin kesuksesan, tetapi tidak mencoba sudah pasti berarti kalah. Komitmen bukan sekadar usaha, tapi kegigihan meski dalam kondisi sulit dan hasil belum terlihat. Saya bersyukur, sekarang bisnis sudah berjalan dengan tim yang solid, dan saya tidak lagi menjalankan operasional sehari-hari. Banyak orang bilang, bisnis yang ideal adalah yang bisa berjalan sendiri sementara pemiliknya bisa jalan-jalan. Saya juga menginginkan hal itu, tapi saya merasa bahwa jalan-jalan tanpa tujuan terasa kurang bermakna. Sekarang, saya menggunakan waktu untuk membuat konten positif, menyebarkan pesan harapan, cinta, dan keyakinan. Konten-konten ini menjadi platform untuk menyampaikan pesan-pesan yang bermakna, dan ini adalah cara saya tetap aktif berkontribusi hingga hari ini.

                        Jangan gunakan kata ganti orang ketiga seperti "penulis", tapi buat seolah-olah Anda yang menyampaikan pesan tersebut secara langsung dan berikan pertanyaan sesuai konten itu untuk memulai diskusi.
                      `);
                }
              }}
              onMouseEnter={() => setIsHoveringGaia(true)}
              onMouseLeave={() => setIsHoveringGaia(false)}
              className="flex items-center justify-center gap-[4px] px-10 pt-4 pb-6 rounded-full"
              style={{
                background: !isHoveringGaia
                  ? "rgba(0, 0, 0, 0.4)"
                  : "linear-gradient(135deg, #883CFF 25%, #2F0F57 100%)",
                backdropFilter: "blur(40px)",
              }}
            >
              <Images
                src="/images/logo-gaia-nobg.png"
                alt="gaia"
                width={40}
                height={18}
              />
            </div>
          </div>
        )}

      {isHoveringGaia && (
        <div className="absolute z-[2] top-60 right-12">
          <div className="relative max-w-[347px] text-black p-8 rounded-xl bg-[rgba(0,0,0,0.72)]">
            <p className="text-b2 font-light text-white_to_white">
              Gaia jelaskan kursus ini
            </p>
            <div
              className="absolute -top-[9.5px] right-24 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent"
              style={{
                borderBottom: "10px solid #000000CC",
              }}
            ></div>
          </div>
        </div>
      )} */}

      <div
        className={`flex gap-8 h-fit ${
          isRow ? "flex-col w-full md:flex-row" : "flex-col"
        }`}
      >
        <div
          className={`relative cursor-pointer overflow-hidden aspect-[16/9]`}
          onMouseEnter={mouseEnterHandler}
          onMouseLeave={mouseLeaveHandler}
          onClick={() => {
            if (isRow) {
              router.push(
                loginState == "LOGIN"
                  ? {
                      pathname: "/course/[courseSlug]",
                      query: { courseSlug: props?.slug },
                    }
                  : {
                      pathname: "/login",
                      query: {
                        url: encodeURIComponent(`/course/${props?.slug}`),
                      },
                    }
              );
            }

            if (tracker) {
              trackEventGTM(tracker);
              trackEventGA(tracker);
            }
          }}
        >
          {(!isHovering || !(props as any)?.hover_content_url) && (
            <>
              {props.image_cover_url ? (
                <Image
                  priority
                  fetchPriority="high"
                  alt={props.name}
                  src={props.image_cover_url}
                  width={305}
                  height={150}
                  data-testid="card-featured_cover"
                  className={`object-cover w-full h-full object-center ${
                    isHovering ? "" : isSquare ? "" : "rounded-8"
                  }`}
                />
              ) : (
                <div
                  className={`bg-neutral-20 w-full aspect-[16/9] ${
                    isRow ? "h-full" : "md:full"
                  } ${isSquare ? "" : "rounded-8"}`}
                ></div>
              )}
            </>
          )}

          {isBusiness &&
            (router as any).pathname == "/pilih-kursus" &&
            !isUncheck && (
              <div
                className={
                  "absolute top-0 w-full h-full flex justify-end items-start p-8"
                }
              >
                <IconPlusCircle />
              </div>
            )}

          {isHovering && (props as any)?.hover_content_url && (
            <div className="w-full h-full">{content()}</div>
          )}

          {isHide && (
            <div className="absolute top-0 bg-[#00000099] w-full h-full flex justify-center items-center">
              <div className="text-b1 font-black text-white">
                Khusus goKampus Business
              </div>
            </div>
          )}

          <div className="absolute bottom-0 w-full h-full px-[8px] py-[6px] bg-gradient-to-t from-[#000] to-transparent rounded-[8px]">
            <p
              className="w-[162px] absolute bottom-4 text-[#fff] text-[14px] font-[500] overflow-hidden text-ellipsis whitespace-pre-wrap"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
              }}
            >
              {props?.name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardCourseSectionMobile;
